/**
 * The active clinic's wall clock (PM-0088).
 *
 * Every date this panel turns into an instant or back — «امروز», a day window, the
 * hour picked on the booking grid — is on the CLINIC's IANA time zone
 * (`Membership.timezone`, owner: clinicmanagement), never on a hard-coded Tehran
 * and never on the browser's own zone. A desk in Baghdad, or a laptop whose clock
 * is set to Europe, must still book 09:00 at the clinic.
 *
 * A module mirror, for the reason clinicSignal.js gives: the date helpers are not
 * inside React and are called from ~40 places. `ClinicContextProvider` writes it
 * on every clinic change and nothing else may. Until a clinic is known it is
 * Asia/Tehran — every clinic that existed before the field.
 */

export const DEFAULT_TIMEZONE = 'Asia/Tehran';
const DAY_MS = 86_400_000;

const validity = new Map();

/** An IANA Area/Location name (or UTC) this browser knows. Never an offset. */
export function isZoneName(name) {
  if (typeof name !== 'string' || !name) return false;
  if (name !== 'UTC' && !name.includes('/')) return false;
  if (validity.has(name)) return validity.get(name);
  let ok = false;
  try {
    ok = new Intl.DateTimeFormat('en-US', { timeZone: name }).resolvedOptions().timeZone === name;
  } catch { ok = false; }
  validity.set(name, ok);
  return ok;
}

let activeZone = DEFAULT_TIMEZONE;

export function getClinicZone() {
  return activeZone;
}

/** Called by ClinicContextProvider when the active clinic changes. Nothing else may call it. */
export function setClinicZone(zone) {
  activeZone = isZoneName(zone) ? zone : DEFAULT_TIMEZONE;
}

const formatters = new Map();
function partsFormatter(zone) {
  let f = formatters.get(zone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-CA', {
      timeZone: zone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hourCycle: 'h23',
    });
    formatters.set(zone, f);
  }
  return f;
}

/** The clinic's wall-clock reading of an instant, as numbers. Null for a bad instant. */
export function zoneParts(ts, zone = activeZone) {
  const d = new Date(Number(ts));
  if (Number.isNaN(d.getTime())) return null;
  const o = {};
  for (const { type, value } of partsFormatter(isZoneName(zone) ? zone : DEFAULT_TIMEZONE).formatToParts(d)) {
    o[type] = value;
  }
  return { y: +o.year, m: +o.month, d: +o.day, hour: +o.hour, minute: +o.minute, second: +o.second };
}

/** The clinic's UTC offset at an instant, in ms. */
export function zoneOffsetMs(ts, zone = activeZone) {
  const p = zoneParts(ts, zone);
  if (!p) return 0;
  return Date.UTC(p.y, p.m - 1, p.d, p.hour, p.minute, p.second) - Math.floor(Number(ts) / 1000) * 1000;
}

const pad2 = (n) => String(n).padStart(2, '0');

/** 'YYYY-MM-DD' — the clinic's calendar day an instant falls in. */
export function isoDayOf(ts, zone = activeZone) {
  const p = zoneParts(ts, zone);
  return p ? `${p.y}-${pad2(p.m)}-${pad2(p.d)}` : '';
}

/**
 * The instant the clinic's clock reads `'YYYY-MM-DD'` + `minutes` past midnight.
 * Two passes, so a wall time across a DST change lands on the right side.
 */
export function wallToMs(isoDay, minutes = 0, zone = activeZone) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoDay || ''));
  if (!m) return NaN;
  const asIfUtc = Date.UTC(+m[1], +m[2] - 1, +m[3]) + Number(minutes) * 60000;
  const first = asIfUtc - zoneOffsetMs(asIfUtc, zone);
  return asIfUtc - zoneOffsetMs(first, zone);
}

/** Calendar arithmetic on 'YYYY-MM-DD' — no clock involved. */
export function addDaysIso(isoDay, days) {
  const t = Date.parse(`${isoDay}T12:00:00Z`) + Number(days) * DAY_MS;
  return new Date(t).toISOString().slice(0, 10);
}

/** The clinic's midnight of the day an instant falls in. */
export function clinicDayStart(ts, zone = activeZone) {
  return wallToMs(isoDayOf(ts, zone), 0, zone);
}

/** The last millisecond of that day — a DST day is 23 or 25 hours long. */
export function clinicDayEnd(ts, zone = activeZone) {
  return wallToMs(addDaysIso(isoDayOf(ts, zone), 1), 0, zone) - 1;
}

/** «امروز» on the clinic's clock, 'YYYY-MM-DD'. */
export function clinicTodayIso(now = Date.now(), zone = activeZone) {
  return isoDayOf(now, zone);
}

/**
 * «Now» shifted onto the clinic's wall clock: read it with getUTC*. The kit's
 * `iranNow()` convention, on the CLINIC's zone instead of a fixed +03:30.
 */
export function clinicNow(now = Date.now(), zone = activeZone) {
  return new Date(Number(now) + zoneOffsetMs(now, zone));
}

/** Minutes past the clinic's midnight. */
export function clinicMinuteOfDay(ts, zone = activeZone) {
  const p = zoneParts(ts, zone);
  return p ? p.hour * 60 + p.minute : NaN;
}
